import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core'
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import {
  CheckoutReplenishmentFormService,
  CheckoutScheduledReplenishmentPlaceOrderComponent
} from '@spartacus/checkout/scheduled-replenishment/components'
import {
  CmsLinkComponent,
  Country,
  GlobalMessageService,
  GlobalMessageType,
  RoutingService,
  StateWithProcess,
  StateWithUser,
  UserIdService,
  UsersSelectors
} from '@spartacus/core'
import { ORDER_TYPE, OrderFacade, ScheduledReplenishmentOrderFacade } from '@spartacus/order/root'
import { LaunchDialogService } from '@spartacus/storefront'
import { CookieService } from 'ngx-cookie-service'
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs'
import { distinctUntilChanged, map, startWith, switchMap, take } from 'rxjs/operators'
import { EbAvailabilityService } from 'src/app/features/product/services/facades/eb-availability.service'
import { EbCart } from '../../../cart/interface/eb-cart.interface'
import { EbCartAdapter } from '../../../cart/services/connectors/eb-cart.adapter'
import { EbBaseSiteService } from '../../../site-context/services/eb-base-site.service'
import { EbUserAccountService } from '../../../user/services/eb-user-account.service'
import { EndCustomerType } from '../../interface/eb-checkout.interface'
import { EbCheckoutService } from '../../services/facades/eb-checkout.service'

@Component({
    selector: 'eb-place-order',
    templateUrl: './eb-place-order.component.html',
    styleUrls: ['./eb-place-order.component.scss'],
    standalone: false
})
export class EbPlaceOrderComponent
  extends CheckoutScheduledReplenishmentPlaceOrderComponent
  implements OnDestroy
{
  @ViewChild('acceptTermsAndConditionsLink') checkbox: ElementRef
  requestPending: boolean
  linkComponents = ['acceptTermsAndConditionsLink', 'acceptExportControlProvisionsLink']
  links$: Observable<CmsLinkComponent[]> = this.routingService.getRouterState().pipe(
    switchMap(routerState =>
      this.ebCheckoutService.getLinkComponentsById(this.linkComponents, routerState.state.context)
    ),
    switchMap(source => of(this.sortCmsLinkComponentsAlphabeticallyInReverse(source)))
  )
  countries$: Observable<Country[]> = this.ebCheckoutService.getCountries()
  typesOfEndCustomer$: Observable<EndCustomerType[]> = this.userIdService.getUserId().pipe(
    take(1),
    switchMap(userId => this.ebCheckoutService.getTypesOfEndCustomer(userId))
  )
  expocoBusinessPartnerSelectionRequired = false
  protected readonly subscriptions = new Subscription()
  public isValidatingShippingDate$ = new BehaviorSubject(false)
  public isPlaceOrderValid$=new BehaviorSubject<boolean>(false);

  checkoutSubmitForm: UntypedFormGroup = this.fb.group({
    acceptTermsAndConditionsLink: new UntypedFormControl(false, [Validators.requiredTrue]),
    acceptExportControlProvisionsLink: new UntypedFormControl(false, [Validators.requiredTrue]),
    expocoTargetCountrySelection: new UntypedFormControl(undefined, [Validators.required]),
    expocoBusinessPartnerSelection: new UntypedFormControl(undefined, [Validators.required])
  })

  public loading = false

  constructor(
    orderFacade: OrderFacade,
    routingService: RoutingService,
    fb: UntypedFormBuilder,
    launchDialogService: LaunchDialogService,
    vcr: ViewContainerRef,
    checkoutReplenishmentFormService: CheckoutReplenishmentFormService,
    scheduledReplenishmentOrderFacade: ScheduledReplenishmentOrderFacade,
    protected activeCartService: ActiveCartFacade,
    protected cookieService: CookieService,
    protected ebCheckoutService: EbCheckoutService,
    protected userIdService: UserIdService,
    protected globalMessageService: GlobalMessageService,
    protected ebForm: ElementRef,
    protected cartAdapter: EbCartAdapter,
    protected cd: ChangeDetectorRef,
    protected userAccountService: EbUserAccountService,
    protected availabilityService: EbAvailabilityService,
    protected store: Store<StateWithUser | StateWithProcess<void>>,
    protected baseSiteService: EbBaseSiteService
  ) {
    super(
      orderFacade,
      routingService,
      fb,
      launchDialogService,
      vcr,
      checkoutReplenishmentFormService,
      scheduledReplenishmentOrderFacade
    )
  }

  ngOnInit() {
    super.ngOnInit();

    this.checkoutSubmitForm.get('expocoTargetCountrySelection')?.setValue('');
    this.checkoutSubmitForm.get('expocoBusinessPartnerSelection')?.setValue(undefined);
    this.checkoutSubmitForm.updateValueAndValidity({ emitEvent: true });

    this.subscribeToCountryChangeEvent();

    this.activeCartService
      .getActive()
      .pipe(
        take(1),
        map(cart => {
          const ebCart = cart as EbCart;
          if (ebCart.expocoBusinessPartner) {
            this.checkoutSubmitForm
              .get('expocoBusinessPartnerSelection')
              .setValue(ebCart.expocoBusinessPartner);
          }
          this.checkoutSubmitForm.updateValueAndValidity({ emitEvent: true });
        })
      )
      .subscribe();

    this.initializePlaceOrderErrorHandling();
    this.checkValidationEvent();

    this.isValidatingShippingDate$.next(true);

    this.subscriptions.add(
      combineLatest([
        this.checkoutSubmitForm.valueChanges.pipe(startWith(this.checkoutSubmitForm.value)),
        this.activeCartService.getActive()
      ])
        .pipe(
          map(([_, cart]) => {
            const valid = this.isPlaceOrderValid(cart as EbCart);
            return valid;
          })
        )
        .subscribe(valid => {
          this.isPlaceOrderValid$.next(valid);
        })
    );
  }

  isPlaceOrderValid(cart: EbCart): boolean {
    const form = this.checkoutSubmitForm;
    const termsValid = form.get('acceptTermsAndConditionsLink')?.valid;
    const exportValid = form.get('acceptExportControlProvisionsLink')?.valid;
    const countrySelection = form.get('expocoTargetCountrySelection');
    const countryValid = countrySelection?.valid && !!countrySelection.value;
    const expocoTargetCountry = !!cart.expocoTargetCountry;

    let partnerValid = true;
    if (this.expocoBusinessPartnerSelectionRequired) {
      const partnerControl = form.get('expocoBusinessPartnerSelection');
      partnerValid = partnerControl?.valid && !!partnerControl.value && !!cart.expocoBusinessPartner;
    }
    return termsValid && exportValid && countryValid && expocoTargetCountry && partnerValid;
  }

  submitForm(): void {
    this.activeCartService
      .getActive()
      .pipe(
        take(1),
        map(cart => {
          if (this.isPlaceOrderValid(cart) && Boolean(this.currentOrderType)) {
            if (ORDER_TYPE.PLACE_ORDER) {
              this.loading = true
              this.ebCheckoutService.placeOrder(
                this.checkoutSubmitForm.get('acceptTermsAndConditionsLink').valid,
                this.checkoutSubmitForm.get('acceptExportControlProvisionsLink').valid
              )
            }
          } else {
            this.checkoutSubmitForm.markAllAsTouched()
            this.globalMessageService.add(
              {
                key: 'checkoutReview.submitOrderButton.validation'
              },
              GlobalMessageType.MSG_TYPE_WARNING
            )
            requestAnimationFrame(() => {
              this.ebForm.nativeElement.querySelector('[error]')?.focus()
            })
          }
        })
      )
      .subscribe()
  }

  sortCmsLinkComponentsAlphabeticallyInReverse(source: CmsLinkComponent[]) {
    return source.sort((a, b) => {
      return a.uid > b.uid ? -1 : 1
    })
  }

  subscribeToCountryChangeEvent() {
    this.subscriptions.add(
      combineLatest([
        this.activeCartService.getActive(),
        this.userIdService.getUserId(),
        this.checkoutSubmitForm
          .get('expocoTargetCountrySelection')
          .valueChanges.pipe(startWith('')),
        this.checkoutSubmitForm
          .get('expocoBusinessPartnerSelection')
          .valueChanges.pipe(startWith(''))
      ])
        .pipe(
          distinctUntilChanged(
            (oldValue, newValue) => oldValue[2] === newValue[2] && oldValue[3] === newValue[3]
          ),
          map(([activeCart, userId, expocoTargetCountry, expocoBusinessPartner]) => {
            const ebCart = activeCart as EbCart
            this.setExpocoBusinessPartnerSelectionVisibility(ebCart, expocoTargetCountry)
            if (expocoTargetCountry !== '') {
              this.requestPending = true;
              this.ebCheckoutService
                .setDestinationCountry(userId, ebCart.code, expocoTargetCountry)
                .subscribe(() => (this.requestPending = false));
            }
            if (
              this.expocoBusinessPartnerSelectionRequired &&
              ebCart.expocoBusinessPartner !== expocoBusinessPartner &&
              expocoBusinessPartner !== ''
            ) {
              this.requestPending = true
              this.ebCheckoutService
                .setTypeOfEndCustomer(userId, ebCart.code, expocoBusinessPartner)
                .subscribe(() => (this.requestPending = false))
            }
          })
        )
        .subscribe()
    )
  }

  setExpocoBusinessPartnerSelectionVisibility(
    activeCart: EbCart,
    expocoTargetCountry: string
  ): void {
    if (
      activeCart.deliveryAddress.country.isocode !== expocoTargetCountry &&
      expocoTargetCountry !== ''
    ) {
      this.expocoBusinessPartnerSelectionRequired = true
    } else {
      this.expocoBusinessPartnerSelectionRequired = false
    }
  }

  convertToFormControl(absCtrl: AbstractControl | null): UntypedFormControl {
    const ctrl = absCtrl as UntypedFormControl
    return ctrl
  }

  initializePlaceOrderErrorHandling(): void {
    // reset loading states to make the UI usable again
    this.subscriptions.add(
      this.cartAdapter.placeOrderError$.subscribe(isError => {
        if (isError) {
          this.loading = false
          this.requestPending = false
          this.cd.detectChanges()
        }
      })
    )
  }

  sortCountriesByName(countries: Country[]): Country[] {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
    return countries && countries.length > 0
      ? countries.sort((a, b) => collator.compare(a.name, b.name))
      : []
  }

  verifyAsAgentHasPlaceOrderPermissionConsents(): Observable<boolean> {
    if (!!this.cookieService.get('asAgent')) {
      return this.baseSiteService.getActiveContext().pipe(
        switchMap(activeContext =>
          this.store.select(
            UsersSelectors.getConsentByTemplateId(
              `ORDER_ON_BEHALF-${activeContext.uid.split('-')[1]}`
            )
          )
        ),
        switchMap(consent => {
          if (!!consent) {
            const isConsentGiven =
              consent.currentConsent && !consent.currentConsent.consentWithdrawnDate
            return of(!isConsentGiven)
          }
        })
      )
    } else {
      return of(false)
    }
  }

  verifyAsAgentHasPlaceOrderUserRole(): Observable<boolean> {
    if (!!this.cookieService.get('asAgent')) {
      return this.userAccountService
        .getEbActiveUserData()
        .pipe(switchMap(userData => of(!userData.asmPlaceOrderAllowed)))
    } else {
      return of(false)
    }
  }

  checkValidationEvent() {
    this.subscriptions.add(
      this.availabilityService.validatingShippingDateEvent.subscribe((validating: boolean) => {
        this.isValidatingShippingDate$.next(validating)
      })
    )
  }

  ngOnDestroy() {
    super.ngOnDestroy()
    this.subscriptions.unsubscribe()
  }
}
